import * as R from 'ramda'
import { computed, ComputedRef, Ref, ref, watch } from 'vue'

import { TeamSide } from '@collector/shared-domain'
import { VolleyballIncidentId } from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'
import { useIncidents, useTeamSides } from '@desktop/views/Relation/composables'

import { useServeQueueIncident } from './useServeQueueIncident'
import { isReceptionHandler, isServeHandler } from './utils'

const forceDisabled = ref(false)

export function useServeIncident(): {
  lastServeParticipantTeamId: Ref<number | null>
  lastServeTeamSide: ComputedRef<TeamSide | null>
  isReceptionVisible: ComputedRef<boolean>
  disabled: ComputedRef<boolean>
  forceDisabled: Ref<boolean>
} {
  // composables
  const { incidents, incidentIdIncidentIndexMap, incidentsUpdate } =
    useIncidents()
  const { getIncidentTeamSide } = useTeamSides()
  const {
    lastServeQueueIncident,
    isLastServeQueueIncidentHandled,
    isLastReceptionQueueIncidentHandled,
    isLastServeIncidentHandled,
    isLastReceptionIncidentHandled,
  } = useServeQueueIncident()

  // state
  const lastServeIncident = ref<Incident | null>(null)

  // computed
  const isLastServeHandled = computed(() => {
    if (isLastServeIncidentsHandled(isServeHandler)) {
      return isLastServeQueueIncidentHandled.value
    }

    // If serve is not handled in history, check if there is a handling incident in queue
    return isLastServeIncidentHandled.value
  })

  const isReceptionVisible = computed(() => {
    if (isLastServeIncidentsHandled(isReceptionHandler)) {
      return !isLastReceptionQueueIncidentHandled.value
    }

    return !isLastReceptionIncidentHandled.value
  })

  const lastServeParticipantTeamId = computed(() => {
    if (lastServeQueueIncident.value) {
      return lastServeQueueIncident.value.payload.participant_team_id || null
    } else if (lastServeIncident.value) {
      return lastServeIncident.value.participant_team_id
    }

    return null
  })

  const lastServeTeamSide = computed(() => {
    if (lastServeParticipantTeamId.value) {
      return getIncidentTeamSide({
        participant_team_id: lastServeParticipantTeamId.value,
      })
    }

    return null
  })

  const disabled = computed(
    () => !isLastServeHandled.value || forceDisabled.value,
  )

  function isLastServeIncidentsHandled(
    isIncidentIdHandler: (incidentId: VolleyballIncidentId) => boolean,
  ): boolean {
    if (!lastServeIncident.value) {
      return true
    }

    const lastServeIncidentIndex = incidentIdIncidentIndexMap.value.get(
      lastServeIncident.value.id,
    )!

    // iterate over incidents after Last Serve incident
    for (let i = lastServeIncidentIndex + 1; i < incidents.value.length; i++) {
      const incident = incidents.value[i]

      // check if any incident finished Last Serve sequence
      if (
        incident.action !== 'delete' &&
        isIncidentIdHandler(incident.incident_id as VolleyballIncidentId)
      ) {
        return true
      }
    }

    return false
  }

  function setLastServeIncident(incidents: Incident[]): void {
    const incident = R.findLast((incident) => {
      return (
        incident.incident_id === VolleyballIncidentId.Serve &&
        incident.action !== 'delete'
      )
    }, incidents)

    if (incident) {
      lastServeIncident.value = incident
    } else if (
      lastServeIncident.value &&
      lastServeIncident.value.action === 'delete' // if there is no Last Serve incident to handle, but previous one was deleted
    ) {
      lastServeIncident.value = null
    }
  }

  // init
  setLastServeIncident(incidents.value)

  watch(incidentsUpdate, () =>
    setLastServeIncident(incidentsUpdate.value.map((i) => i.data)),
  )

  watch(isLastServeHandled, (value) => {
    if (!value) {
      // `value` is `false`
      //
      // New serve occurred and is not handled yet - reset `forceDisabled`
      forceDisabled.value = false

      // `value` becomes `true`
      //
      // last serve is handled
      // `forceDisabled` = `false` allows `disabled` to be `false`
    }
  })

  return {
    lastServeParticipantTeamId,
    lastServeTeamSide,
    isReceptionVisible,
    forceDisabled,
    disabled,
  }
}
